import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import campaignService from '../services/campaignService.js';
import recipientService from '../services/recipientService.js';
import './CreateCampaign.css';

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Campaign data
  const [campaignData, setCampaignData] = useState({
    name: '',
    description: '',
    type: 'both', // linkedin, email, both
    subject: '',
    content: '',
    linkedinMessage: '',
    scheduleType: 'immediate', // immediate, scheduled
    scheduledAt: ''
  });

  // Recipients data
  const [recipients, setRecipients] = useState([]);
  const [recipientInput, setRecipientInput] = useState({
    email: '',
    linkedinUrl: '',
    firstName: '',
    lastName: '',
    company: '',
    title: ''
  });

  const totalSteps = 5;

  const handleInputChange = (field, value) => {
    setCampaignData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRecipientInputChange = (field, value) => {
    setRecipientInput(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addRecipient = () => {
    if (!recipientInput.email && !recipientInput.linkedinUrl) {
      alert('Please enter either email or LinkedIn URL');
      return;
    }

    const newRecipient = {
      ...recipientInput,
      recipientId: `recipient-${Date.now()}`
    };

    setRecipients(prev => [...prev, newRecipient]);
    setRecipientInput({
      email: '',
      linkedinUrl: '',
      firstName: '',
      lastName: '',
      company: '',
      title: ''
    });
  };

  const removeRecipient = (index) => {
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!campaignData.name || campaignData.name.trim().length < 2) {
          setError('Campaign name must be at least 2 characters');
          return false;
        }
        break;
      case 2:
        if (campaignData.type === 'email' || campaignData.type === 'both') {
          if (!campaignData.subject || !campaignData.content) {
            setError('Email campaigns require subject and content');
            return false;
          }
        }
        if (campaignData.type === 'linkedin' || campaignData.type === 'both') {
          if (!campaignData.linkedinMessage) {
            setError('LinkedIn campaigns require a message');
            return false;
          }
        }
        break;
      case 3:
        if (recipients.length === 0) {
          setError('Please add at least one recipient');
          return false;
        }
        break;
      case 4:
        if (campaignData.scheduleType === 'scheduled' && !campaignData.scheduledAt) {
          setError('Please select a scheduled date and time');
          return false;
        }
        break;
    }
    setError(null);
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create campaign
      const campaign = await campaignService.createCampaign({
        ...campaignData,
        status: 'draft'
      });

      // Add recipients
      if (recipients.length > 0) {
        await recipientService.addRecipients(campaign.campaignId, recipients);
      }

      // Navigate to campaign detail
      navigate(`/marketing/${campaign.campaignId}`);
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h3>Campaign Information</h3>
            <div className="form-group">
              <label htmlFor="campaign-name">Campaign Name *</label>
              <input
                id="campaign-name"
                type="text"
                value={campaignData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Q1 2025 Outreach"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="campaign-description">Description</label>
              <textarea
                id="campaign-description"
                value={campaignData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description of the campaign"
                rows="4"
              />
            </div>
            <div className="form-group">
              <label htmlFor="campaign-type">Campaign Type *</label>
              <select
                id="campaign-type"
                value={campaignData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                required
              >
                <option value="linkedin">LinkedIn Only</option>
                <option value="email">Email Only</option>
                <option value="both">Both LinkedIn & Email</option>
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h3>Campaign Content</h3>
            {(campaignData.type === 'email' || campaignData.type === 'both') && (
              <>
                <div className="form-group">
                  <label htmlFor="email-subject">Email Subject *</label>
                  <input
                    id="email-subject"
                    type="text"
                    value={campaignData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Email subject line"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email-content">Email Content *</label>
                  <textarea
                    id="email-content"
                    value={campaignData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    placeholder="Email body content (HTML supported)"
                    rows="8"
                    required
                  />
                </div>
              </>
            )}
            {(campaignData.type === 'linkedin' || campaignData.type === 'both') && (
              <div className="form-group">
                <label htmlFor="linkedin-message">LinkedIn Message *</label>
                <textarea
                  id="linkedin-message"
                  value={campaignData.linkedinMessage}
                  onChange={(e) => handleInputChange('linkedinMessage', e.target.value)}
                  placeholder="LinkedIn message content"
                  rows="8"
                  required
                />
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h3>Recipients</h3>
            <div className="recipient-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recipient-email">Email</label>
                  <input
                    id="recipient-email"
                    type="email"
                    value={recipientInput.email}
                    onChange={(e) => handleRecipientInputChange('email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="recipient-linkedin">LinkedIn URL</label>
                  <input
                    id="recipient-linkedin"
                    type="url"
                    value={recipientInput.linkedinUrl}
                    onChange={(e) => handleRecipientInputChange('linkedinUrl', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recipient-firstname">First Name</label>
                  <input
                    id="recipient-firstname"
                    type="text"
                    value={recipientInput.firstName}
                    onChange={(e) => handleRecipientInputChange('firstName', e.target.value)}
                    placeholder="John"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="recipient-lastname">Last Name</label>
                  <input
                    id="recipient-lastname"
                    type="text"
                    value={recipientInput.lastName}
                    onChange={(e) => handleRecipientInputChange('lastName', e.target.value)}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recipient-company">Company</label>
                  <input
                    id="recipient-company"
                    type="text"
                    value={recipientInput.company}
                    onChange={(e) => handleRecipientInputChange('company', e.target.value)}
                    placeholder="Company Name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="recipient-title">Title</label>
                  <input
                    id="recipient-title"
                    type="text"
                    value={recipientInput.title}
                    onChange={(e) => handleRecipientInputChange('title', e.target.value)}
                    placeholder="Job Title"
                  />
                </div>
              </div>
              <button type="button" className="btn btn-secondary" onClick={addRecipient}>
                ➕ Add Recipient
              </button>
            </div>

            {recipients.length > 0 && (
              <div className="recipients-list">
                <h4>Added Recipients ({recipients.length})</h4>
                <div className="recipients-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>LinkedIn</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map((recipient, index) => (
                        <tr key={index}>
                          <td>{recipient.email || '-'}</td>
                          <td>{recipient.linkedinUrl || '-'}</td>
                          <td>{recipient.firstName} {recipient.lastName}</td>
                          <td>{recipient.company || '-'}</td>
                          <td>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => removeRecipient(index)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h3>Schedule</h3>
            <div className="form-group">
              <label htmlFor="schedule-type">Schedule Type *</label>
              <select
                id="schedule-type"
                value={campaignData.scheduleType}
                onChange={(e) => handleInputChange('scheduleType', e.target.value)}
                required
              >
                <option value="immediate">Send Immediately</option>
                <option value="scheduled">Schedule for Later</option>
              </select>
            </div>
            {campaignData.scheduleType === 'scheduled' && (
              <div className="form-group">
                <label htmlFor="scheduled-at">Scheduled Date & Time *</label>
                <input
                  id="scheduled-at"
                  type="datetime-local"
                  value={campaignData.scheduledAt}
                  onChange={(e) => handleInputChange('scheduledAt', e.target.value)}
                  required
                />
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h3>Review & Create</h3>
            <div className="review-section">
              <h4>Campaign Details</h4>
              <div className="review-item">
                <strong>Name:</strong> {campaignData.name}
              </div>
              <div className="review-item">
                <strong>Type:</strong> {campaignData.type === 'both' ? 'LinkedIn & Email' : campaignData.type}
              </div>
              <div className="review-item">
                <strong>Schedule:</strong> {campaignData.scheduleType === 'immediate' ? 'Send Immediately' : `Scheduled for ${campaignData.scheduledAt}`}
              </div>
              <div className="review-item">
                <strong>Recipients:</strong> {recipients.length}
              </div>
            </div>
            {campaignData.description && (
              <div className="review-section">
                <h4>Description</h4>
                <p>{campaignData.description}</p>
              </div>
            )}
            {(campaignData.type === 'email' || campaignData.type === 'both') && (
              <div className="review-section">
                <h4>Email Content</h4>
                <div className="review-item">
                  <strong>Subject:</strong> {campaignData.subject}
                </div>
                <div className="review-content">
                  {campaignData.content}
                </div>
              </div>
            )}
            {(campaignData.type === 'linkedin' || campaignData.type === 'both') && (
              <div className="review-section">
                <h4>LinkedIn Message</h4>
                <div className="review-content">
                  {campaignData.linkedinMessage}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="create-campaign-page">
      <div className="page-header">
        <h1>Create New Campaign</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/marketing')}>
          ← Cancel
        </button>
      </div>

      <div className="wizard-container">
        {/* Step Indicator */}
        <div className="step-indicator">
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className={`step ${step === currentStep ? 'active' : step < currentStep ? 'completed' : ''}`}>
              <div className="step-number">{step}</div>
              <div className="step-label">
                {step === 1 && 'Info'}
                {step === 2 && 'Content'}
                {step === 3 && 'Recipients'}
                {step === 4 && 'Schedule'}
                {step === 5 && 'Review'}
              </div>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="wizard-content">
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="wizard-actions">
            {currentStep > 1 && (
              <button className="btn btn-secondary" onClick={handleBack} disabled={loading}>
                ← Back
              </button>
            )}
            <div className="wizard-actions-right">
              {currentStep < totalSteps ? (
                <button className="btn btn-primary" onClick={handleNext} disabled={loading}>
                  Next →
                </button>
              ) : (
                <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Creating...' : 'Create Campaign'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaign;

